/**
 * Shared Creator Fees Util
 *
 * Single canonical source for "all claimable creator fees" across hooks.
 * Sums DBC bonding-curve creator fees + DAMM V2 locked-position fees from
 * graduated coins. Both useCreatorFeesQuery (dropdown / widget) and
 * useCreatorFees (page) call this so totals can never drift.
 */

import * as Sentry from '@sentry/react';
import BN from 'bn.js';
import { Connection, PublicKey } from '@solana/web3.js';
import {
  FeeMetricsClient,
  PoolFeeMetrics,
  hasClaimableCreatorFees,
} from '@/lib/meteora/feeMetricsClient';
import {
  fetchDammV2PositionFees,
  type DammV2PositionFees,
} from '@/lib/meteora/dammV2FeeReader';
import { supabase } from '@/integrations/supabase/client';

export interface CreatorTokenWithDammV2 {
  id: string;
  mint_address: string;
  pool_address: string | null;
  damm_pool_address: string | null;
  creator_position_address: string | null;
  name: string;
  symbol: string;
  image_url: string | null;
  created_at: string;
  description: string | null;
  website_url: string | null;
  twitter_handle: string | null;
  creator_address: string;
}

export interface CreatorFeeSnapshot {
  tokens: CreatorTokenWithDammV2[];
  bcMetrics: Map<string, PoolFeeMetrics | null>;       // key: pool_address
  dammV2Fees: Map<string, DammV2PositionFees | null>;  // key: mint_address
  totalClaimable: BN;
  claimablePoolsCount: number;
}

export const DAMM_V2_FETCH_CONCURRENCY = 8;

export interface FeeFetchContext {
  rpcProvider: string;
  environment: string;
  hook: 'useCreatorFeesQuery' | 'useCreatorFees';
}

const SELECT_COLS =
  'id, mint_address, pool_address, damm_pool_address, creator_position_address, name, symbol, image_url, created_at, description, website_url, twitter_handle, creator_address';

export async function fetchCreatorTokens(
  creatorAddress: string,
  ctx: FeeFetchContext,
): Promise<CreatorTokenWithDammV2[]> {
  const { data, error } = await supabase
    .from('launched_tokens')
    .select(SELECT_COLS)
    .eq('creator_address', creatorAddress)
    .order('created_at', { ascending: false });

  if (error) {
    Sentry.captureException(error, {
      tags: {
        feature: 'creator-fees',
        hook: ctx.hook,
        op: 'fetch_tokens',
        rpc_provider: ctx.rpcProvider,
        environment: ctx.environment,
      },
    });
    return [];
  }

  return (data ?? []) as CreatorTokenWithDammV2[];
}

export async function fetchSingleBcMetric(
  poolAddress: string,
  feeClient: FeeMetricsClient,
): Promise<PoolFeeMetrics | null> {
  return feeClient.fetchPoolFeeMetrics(new PublicKey(poolAddress));
}

export async function fetchSingleDammV2Fee(
  poolAddress: string,
  positionAddress: string,
  connection: Connection,
): Promise<DammV2PositionFees | null> {
  const fees = await fetchDammV2PositionFees(
    connection,
    new PublicKey(poolAddress),
    [new PublicKey(positionAddress)],
  );
  return fees[0] ?? null;
}

async function fetchDammV2FeesBatch(
  graduated: CreatorTokenWithDammV2[],
  connection: Connection,
  ctx: FeeFetchContext,
): Promise<Map<string, DammV2PositionFees | null>> {
  const result = new Map<string, DammV2PositionFees | null>();
  const targets = graduated.filter(
    (t) => t.damm_pool_address && t.creator_position_address,
  );
  if (targets.length === 0) return result;

  let next = 0;
  const workers = Array.from(
    { length: Math.min(DAMM_V2_FETCH_CONCURRENCY, targets.length) },
    async () => {
      while (true) {
        const i = next++;
        if (i >= targets.length) return;
        const t = targets[i];
        try {
          const fee = await fetchSingleDammV2Fee(
            t.damm_pool_address!,
            t.creator_position_address!,
            connection,
          );
          result.set(t.mint_address, fee);
        } catch (err) {
          result.set(t.mint_address, null);
          Sentry.captureException(err, {
            level: 'error',
            tags: {
              feature: 'creator-fees',
              hook: ctx.hook,
              op: 'damm_v2_fee_fetch',
              rpc_provider: ctx.rpcProvider,
              mint_address: t.mint_address,
              environment: ctx.environment,
            },
          });
        }
      }
    },
  );

  await Promise.all(workers);
  return result;
}

export function computeClaimableTotals(
  bcMetrics: Map<string, PoolFeeMetrics | null>,
  dammV2Fees: Map<string, DammV2PositionFees | null>,
): { totalClaimable: BN; claimablePoolsCount: number } {
  let totalClaimable = new BN(0);
  let count = 0;

  for (const m of bcMetrics.values()) {
    if (m && hasClaimableCreatorFees(m)) {
      totalClaimable = totalClaimable.add(m.creatorQuoteFee);
      count += 1;
    }
  }

  for (const f of dammV2Fees.values()) {
    if (f && !f.claimableFeeB.isZero()) {
      totalClaimable = totalClaimable.add(f.claimableFeeB);
      count += 1;
    }
  }

  return { totalClaimable, claimablePoolsCount: count };
}

export async function fetchAllCreatorClaimableFees(
  creatorAddress: string,
  feeClient: FeeMetricsClient,
  ctx: FeeFetchContext,
): Promise<CreatorFeeSnapshot> {
  const tokens = await fetchCreatorTokens(creatorAddress, ctx);
  const poolAddrs = tokens
    .map((t) => t.pool_address)
    .filter((a): a is string => !!a);

  const [bcMetrics, dammV2Fees] = await Promise.all([
    poolAddrs.length === 0
      ? Promise.resolve(new Map<string, PoolFeeMetrics | null>())
      : feeClient.fetchBatchPoolFeeMetricsFast(
          poolAddrs.map((a) => new PublicKey(a)),
        ),
    fetchDammV2FeesBatch(tokens, feeClient.getConnection(), ctx),
  ]);

  const { totalClaimable, claimablePoolsCount } = computeClaimableTotals(
    bcMetrics,
    dammV2Fees,
  );

  return { tokens, bcMetrics, dammV2Fees, totalClaimable, claimablePoolsCount };
}

export function getRpcProviderTag(): string {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (supabaseUrl) return 'supabase_rpc_proxy';
  return 'unknown';
}

export function getEnvironmentTag(): string {
  return import.meta.env.MODE ?? 'unknown';
}
